/**
 * TableCellStyleExtension — Extends TableCellPlus / TableHeaderPlus / TableRowPlus
 * from tiptap-table-plus to support additional cell styling attributes:
 *   - background (cell fill color)
 *   - borderTop / borderBottom / borderLeft / borderRight
 *   - verticalAlign (top / middle / bottom)
 *   - paddingTop / paddingBottom / paddingLeft / paddingRight
 *   - Row height
 *
 * These attributes are parsed from OOXML by tableParser.ts and rendered
 * via custom addNodeView overrides (since TableCellPlus bypasses renderHTML).
 */

import { TableCellPlus } from "tiptap-table-plus/dist/pagination/TableCellPlus";
import { TableHeaderPlus } from "tiptap-table-plus/dist/pagination/TableHeaderPlus";
import { TableRowPlus } from "tiptap-table-plus/dist/pagination/TableRowPlus";

/**
 * Unique ID attribute for table cells/headers/rows.
 * Must be explicitly added here because addGlobalAttributes from
 * UniqueIdExtension doesn't get merged into tiptap-table-plus node types.
 */
const nodeIdAttribute = {
  id: {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute("data-node-id"),
    renderHTML: (attributes: Record<string, unknown>) => {
      if (!attributes.id) return {};
      return { "data-node-id": attributes.id };
    },
  },
};

/** Shared attribute definitions for both tableCell and tableHeader */
const cellStyleAttributes = {
  ...nodeIdAttribute,
  background: {
    default: null,
    parseHTML: (element: HTMLElement) =>
      element.style.backgroundColor || element.getAttribute("data-background") || null,
    renderHTML: (attributes: Record<string, unknown>) => {
      if (!attributes.background) return {};
      return { style: `background-color: ${attributes.background}` };
    },
  },
  borderTop: {
    default: null,
    parseHTML: (element: HTMLElement) =>
      element.getAttribute("data-border-top") || null,
    renderHTML: (attributes: Record<string, unknown>) => {
      if (!attributes.borderTop) return {};
      return { "data-border-top": attributes.borderTop };
    },
  },
  borderBottom: {
    default: null,
    parseHTML: (element: HTMLElement) =>
      element.getAttribute("data-border-bottom") || null,
    renderHTML: (attributes: Record<string, unknown>) => {
      if (!attributes.borderBottom) return {};
      return { "data-border-bottom": attributes.borderBottom };
    },
  },
  borderLeft: {
    default: null,
    parseHTML: (element: HTMLElement) =>
      element.getAttribute("data-border-left") || null,
    renderHTML: (attributes: Record<string, unknown>) => {
      if (!attributes.borderLeft) return {};
      return { "data-border-left": attributes.borderLeft };
    },
  },
  borderRight: {
    default: null,
    parseHTML: (element: HTMLElement) =>
      element.getAttribute("data-border-right") || null,
    renderHTML: (attributes: Record<string, unknown>) => {
      if (!attributes.borderRight) return {};
      return { "data-border-right": attributes.borderRight };
    },
  },
  verticalAlign: {
    default: null,
    parseHTML: (element: HTMLElement) =>
      element.style.verticalAlign || element.getAttribute("data-vertical-align") || null,
    renderHTML: (attributes: Record<string, unknown>) => {
      if (!attributes.verticalAlign) return {};
      return { style: `vertical-align: ${attributes.verticalAlign}` };
    },
  },
  paddingTop: {
    default: null,
    parseHTML: (element: HTMLElement) => {
      const v = element.style.paddingTop;
      return v ? parseFloat(v) : null;
    },
  },
  paddingBottom: {
    default: null,
    parseHTML: (element: HTMLElement) => {
      const v = element.style.paddingBottom;
      return v ? parseFloat(v) : null;
    },
  },
  paddingLeft: {
    default: null,
    parseHTML: (element: HTMLElement) => {
      const v = element.style.paddingLeft;
      return v ? parseFloat(v) : null;
    },
  },
  paddingRight: {
    default: null,
    parseHTML: (element: HTMLElement) => {
      const v = element.style.paddingRight;
      return v ? parseFloat(v) : null;
    },
  },
};

/**
 * Apply style attributes to a cell DOM element.
 * Called from both addNodeView (initial render) and update (attr changes).
 */
function applyCellStyles(
  dom: HTMLElement,
  attrs: Record<string, unknown>
) {
  // Background
  if (attrs.background) {
    dom.style.backgroundColor = attrs.background as string;
  } else {
    dom.style.backgroundColor = "";
  }

  // Borders — format: "1pt solid #000000"
  // Convert to CSS: the format is already CSS-compatible
  if (attrs.borderTop) {
    dom.style.borderTop = attrs.borderTop as string;
  }
  if (attrs.borderBottom) {
    dom.style.borderBottom = attrs.borderBottom as string;
  }
  if (attrs.borderLeft) {
    dom.style.borderLeft = attrs.borderLeft as string;
  }
  if (attrs.borderRight) {
    dom.style.borderRight = attrs.borderRight as string;
  }

  // Vertical alignment
  if (attrs.verticalAlign) {
    dom.style.verticalAlign = attrs.verticalAlign as string;
    // For grid-based layout used by tiptap-table-plus, verticalAlign on td
    // doesn't work. Use flexbox alignment instead.
    dom.style.display = "flex";
    dom.style.flexDirection = "column";
    const alignMap: Record<string, string> = {
      top: "flex-start",
      middle: "center",
      bottom: "flex-end",
    };
    dom.style.justifyContent = alignMap[attrs.verticalAlign as string] || "flex-start";
  }

  // Padding
  if (attrs.paddingTop != null) {
    dom.style.paddingTop = `${attrs.paddingTop}px`;
  }
  if (attrs.paddingBottom != null) {
    dom.style.paddingBottom = `${attrs.paddingBottom}px`;
  }
  if (attrs.paddingLeft != null) {
    dom.style.paddingLeft = `${attrs.paddingLeft}px`;
  }
  if (attrs.paddingRight != null) {
    dom.style.paddingRight = `${attrs.paddingRight}px`;
  }
}

/**
 * Extended TableCell with styling support.
 */
export const StyledTableCell = TableCellPlus.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...cellStyleAttributes,
    };
  },

  addNodeView() {
    return ({ node }) => {
      const tableNode =
        this.editor.extensionManager.extensions.find(
          (extension) => extension.name === "table"
        );
      const borderColor = tableNode
        ? tableNode.options.borderColor
        : "black";
      const dom = document.createElement("td");
      dom.style.border = `1px solid ${borderColor}`;

      let colspan = node.attrs.colspan;
      let rowspan = node.attrs.rowspan;

      const updateGrid = (c: number, r: number) => {
        dom.style.gridColumn = `auto / span ${c || 1}`;
        dom.rowSpan = r || 1;
        dom.setAttribute("colspan", `${c || 1}`);
      };
      updateGrid(colspan, rowspan);

      // Set node ID for AI agent addressing (addNodeView bypasses renderHTML)
      if (node.attrs.id) {
        dom.setAttribute("data-node-id", node.attrs.id);
      }

      // Apply custom styles
      applyCellStyles(dom, node.attrs);

      return {
        dom,
        contentDOM: dom,
        update(updatedNode) {
          if (updatedNode.type.name !== "tableCell") {
            return false;
          }
          const updatedColspan = updatedNode.attrs.colspan;
          const updatedRowspan = updatedNode.attrs.rowspan;
          if (updatedColspan !== colspan || updatedRowspan !== rowspan) {
            colspan = updatedColspan;
            rowspan = updatedRowspan;
            updateGrid(updatedColspan, updatedRowspan);
          }
          // Update node ID
          if (updatedNode.attrs.id) {
            dom.setAttribute("data-node-id", updatedNode.attrs.id);
          }
          // Re-apply styles on update
          applyCellStyles(dom, updatedNode.attrs);
          return true;
        },
      };
    };
  },
});

/**
 * Extended TableHeader with styling support.
 */
export const StyledTableHeader = TableHeaderPlus.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...cellStyleAttributes,
    };
  },

  addNodeView() {
    return ({ node }) => {
      const tableNode =
        this.editor.extensionManager.extensions.find(
          (extension) => extension.name === "table"
        );
      const borderColor = tableNode
        ? tableNode.options.borderColor
        : "black";
      const dom = document.createElement("th");
      dom.style.border = `1px solid ${borderColor}`;

      let colspan = node.attrs.colspan;
      let rowspan = node.attrs.rowspan;

      const updateGrid = (c: number, r: number) => {
        dom.style.gridColumn = `auto / span ${c || 1}`;
        dom.rowSpan = r || 1;
        dom.setAttribute("colspan", `${c || 1}`);
      };
      updateGrid(colspan, rowspan);

      // Set node ID for AI agent addressing (addNodeView bypasses renderHTML)
      if (node.attrs.id) {
        dom.setAttribute("data-node-id", node.attrs.id);
      }

      // Apply custom styles
      applyCellStyles(dom, node.attrs);

      return {
        dom,
        contentDOM: dom,
        update(updatedNode) {
          if (updatedNode.type.name !== "tableHeader") {
            return false;
          }
          const updatedColspan = updatedNode.attrs.colspan;
          const updatedRowspan = updatedNode.attrs.rowspan;
          if (updatedColspan !== colspan || updatedRowspan !== rowspan) {
            colspan = updatedColspan;
            rowspan = updatedRowspan;
            updateGrid(updatedColspan, updatedRowspan);
          }
          // Update node ID
          if (updatedNode.attrs.id) {
            dom.setAttribute("data-node-id", updatedNode.attrs.id);
          }
          applyCellStyles(dom, updatedNode.attrs);
          return true;
        },
      };
    };
  },
});

/**
 * Extended TableRow with height support.
 */
export const StyledTableRow = TableRowPlus.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...nodeIdAttribute,
      height: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const h = element.style.height;
          return h ? parseFloat(h) : null;
        },
      },
    };
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement("tr");
      dom.style.display = "grid";
      dom.style.gridTemplateColumns = `var(--cell-percentage)`;
      dom.style.position = "relative";

      // Apply row height
      if (node.attrs.height) {
        dom.style.minHeight = `${node.attrs.height}px`;
      }

      return {
        dom,
        contentDOM: dom,
        update(updatedNode) {
          if (updatedNode.type.name !== "tableRow") {
            return false;
          }
          if (updatedNode.attrs.height) {
            dom.style.minHeight = `${updatedNode.attrs.height}px`;
          } else {
            dom.style.minHeight = "";
          }
          return true;
        },
      };
    };
  },
});
